import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
  Activity,
  Building2,
  CalendarDays,
  Car,
  Clock3,
  Cpu,
  Database,
  Eye,
  Server,
  ShieldCheck,
  Truck,
  Users,
} from 'lucide-react';
import { SkeletonPage } from '../ui/kit';
import './Maintenance.css';
import PageHeader from '../components/PageHeader';
import { datumUndZeit } from '../lib/zeit';

const fmtUptime = (s) => {
  if (s == null) return '–';
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  return [d ? `${d} T` : null, h ? `${h} h` : null, `${m} Min`].filter(Boolean).join(' ');
};

const startedAt = (value) => (value ? datumUndZeit(value, { jahr: false }) : null);

function SystemMetric({ icon: Icon, label, value, sub, tone = 'default' }) {
  return (
    <article className={`maintenance-metric is-${tone}`}>
      <div className="maintenance-metric-icon"><Icon size={20} /></div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        {sub && <small>{sub}</small>}
      </div>
    </article>
  );
}

function InventoryMetric({ icon: Icon, label, value, detail }) {
  return (
    <article className="maintenance-inventory-card">
      <div className="maintenance-inventory-icon"><Icon size={18} /></div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{detail}</small>
      </div>
    </article>
  );
}

export default function Maintenance() {
  const [d, setD] = useState(null);

  useEffect(() => {
    const load = () => api.get('/admin/system-info').then(r => setD(r.data)).catch(() => {});
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);

  if (!d) return <SkeletonPage />;
  const c = d.counts || {};
  const started = startedAt(d.started_at);

  return (
    <div className="maintenance-page">
      <PageHeader
        title="Wartung &amp; System"
        description="Versions-, Laufzeit- und Bestandsübersicht des VOOGO Backends – strikt ohne operative Eingriffe."
        actions={
        <div className="maintenance-hero-badges" aria-label="Datenvertrag">
          <span><Eye size={14} /> Nur lesen</span>
          <span><Activity size={14} /> Aktualisierung · 15 s</span>
        </div>
      }
      />

      <section className="maintenance-status">
        <div className="maintenance-status-icon"><Server size={24} /></div>
        <div className="maintenance-status-copy">
          <span>Backend Runtime</span>
          <h2>Systeminformationen verfügbar</h2>
          <p>Der Admin liest den bestehenden System-Info-Vertrag. Diese Oberfläche führt keine Wartungsaktion aus.</p>
        </div>
        <div className="maintenance-status-meta">
          <span><i /> Live-Abgleich</span>
          <small>zuletzt aus /admin/system-info</small>
        </div>
      </section>

      <section className="maintenance-metric-grid" aria-label="Systemkennzahlen">
        <SystemMetric
          icon={ShieldCheck}
          label="Backend-Version"
          value={`v${d.version}`}
          sub={`API ${d.api}`}
          tone="purple"
        />
        <SystemMetric
          icon={Cpu}
          label="Node Runtime"
          value={d.node || '–'}
          sub="Server-Laufzeitumgebung"
        />
        <SystemMetric
          icon={Clock3}
          label="Laufzeit"
          value={fmtUptime(d.uptime_sec)}
          sub={started ? `gestartet ${started}` : 'Startzeit nicht verfügbar'}
          tone="green"
        />
        <SystemMetric
          icon={Database}
          label="Datenbank"
          value={d.db_size_mb != null ? `${d.db_size_mb} MB` : '–'}
          sub={d.db_name || 'voogo.db'}
        />
      </section>

      <section className="maintenance-section">
        <div className="maintenance-section-heading">
          <div className="maintenance-heading-index">01</div>
          <div>
            <span>Live-Bestand</span>
            <h2>Systemvolumen</h2>
            <p>Verdichtete Zähler aus derselben read-only Systemabfrage.</p>
          </div>
          <div className="maintenance-source-pill"><Database size={14} /> Backend Source of Truth</div>
        </div>

        <div className="maintenance-inventory-grid">
          <InventoryMetric icon={Car} label="Fahrten gesamt" value={c.rides ?? '–'} detail="gesamter Bestand" />
          <InventoryMetric icon={CalendarDays} label="Fahrten heute" value={c.rides_today ?? '–'} detail="Betriebstag 04:00–03:59 · Europe/Berlin" />
          <InventoryMetric icon={Truck} label="Fahrer" value={c.drivers ?? '–'} detail="registrierte Konten" />
          <InventoryMetric icon={Users} label="Kunden" value={c.users ?? '–'} detail="registrierte Konten" />
          <InventoryMetric icon={Building2} label="Partner" value={c.partners ?? '–'} detail="Flottenpartner" />
        </div>
      </section>

      <footer className="maintenance-boundary">
        <Eye size={19} />
        <div>
          <strong>Beobachtungsfläche ohne Mutation</strong>
          <p>Kein Neustart, keine Bereinigung, kein Backup und keine Datenänderung werden von dieser Seite ausgelöst.</p>
        </div>
        <span>Read-only</span>
      </footer>
    </div>
  );
}
